import React from 'react';
import { isEmpty } from 'lodash';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';

import { TableView } from './TableView';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { MetadataContext } from 'contexts/MetadataContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { UploadStateContext } from 'contexts/UploadStateContext';
import { ApiDataContext } from 'contexts/ApiDataContext';
import { convertToArray } from 'Details/utils';
import { CellModalEditor } from './CellModalEditor';
import CategorizationDialog from './CategorisationDialog';
import { ErrorMessageModal } from './ErrorMessageModal';
import SecondaryNav from 'SecondaryNav';
import { isCustomCategory, EMPTY_VALUE_NULL } from 'Details/utils';
import range from 'lodash/range';

import { Wrapper, LoaderContainer, TableContainer } from './styles';
import { chartFilterColumns } from './tableMetadata';
import { getCatgoriesForFilter } from 'utils/apiRequests';

const DEFAULT_DIMENSION = 'category';

export class PivotTable extends React.Component {
	state = {
		isCellModalVisible: false,
		isCategorisationModalVisible: false,
		cellModalEditorProps: {},
		categoriesOptions: [],
		isCategoriesOptionsLoading: true,
	};

	componentDidMount() {
		this.setInitialQueryStringState();
		this.getCategories();
	}

	setInitialQueryStringState = () => {
		const { contextParams } = this.props;
		const { dimensions } = contextParams;

		const getDimensionLevels = (dimensions, dimensionName) => {
			const dimensionItem = dimensions.find(
				item => item.name === dimensionName
			);
			return dimensionItem ? dimensionItem.levels : 1;
		};
		const getInitialDimensions = dimensionName => {
			const defaultDimensionLevels = getDimensionLevels(
				this.props.dimensionOptions,
				dimensionName
			);
			return range(defaultDimensionLevels).map(() => dimensionName);
		};

		contextParams.setQueryStringStateAndReplace({
			dimensions: dimensions || getInitialDimensions(DEFAULT_DIMENSION),
		});
	};

	getCategories = async () => {
		const response = await getCatgoriesForFilter();
		if (response.succeeded) {
			if (response.data !== null) {
				if (Object.keys(response.data).length > 0) {
					const keys = Object.keys(response.data);
					const data = keys.map(x => {
						let word = x[0].toUpperCase() + x.slice(1);
						return {
							name: word,
							label: word,
							columnName: word,
							values: response.data[x].map(v => {
								return { label: v, value: v };
							}),
						};
					});
					this.setState({ categoriesOptions: data });
				}
			}
		}
		this.setState({ isCategoriesOptionsLoading: false });
	};

	closeCellModalEditor = () => {
		this.setState({
			isCellModalVisible: false,
			isCategorisationModalVisible: false,
			cellModalEditorProps: {},
		});
	};

	openCellModalEditor = cellModalEditorProps => {
		this.setState({
			isCellModalVisible: true,
			cellModalEditorProps,
		});
	};

	openCategorisationModal = cellModalEditorProps => {
		this.setState({
			isCategorisationModalVisible: true,
			cellModalEditorProps,
		});
	};

	setIsTaxonomyTableReady = isReady => {
		this.setState({ isTaxonomyTableReady: isReady });
	};

	showErrorMessage = errorMessageText => {
		this.setState({ isErrorMessageVisible: true, errorMessageText });
	};

	hideErrorMessage = () => {
		this.setState({ isErrorMessageVisible: false, errorMessageText: null });
	};

	getCustomFilters() {
		const { contextParams } = this.props;
		let {
			storedQueryId,
			customFiltersValues,
			customFilters,
			storedQuery,
		} = contextParams;

		if (storedQueryId) {
			if (storedQuery) {
				({ customFiltersValues, customFilters } = storedQuery);

				customFiltersValues = customFiltersValues.map(
					item => (item === '' || item === null ? EMPTY_VALUE_NULL : item)
				);
			} else {
				customFiltersValues = [];
				customFilters = [];
			}
		}

		return { customFiltersValues, customFilters };
	}

	renderTable() {
		const {
			isTaxonomyTableReady,
			categoriesOptions,
			isCategoriesOptionsLoading,
		} = this.state;
		const {
			contextParams,
			customFilterOptions,
			clearApiCache,
			apiCacheTrigger,
			permissions,
			status,
			companiesList,
		} = this.props;
		const {
			dateFrom,
			dateTo,
			showNonSourceable,
			hierarchy,
			measures,
			dimensions,
			showHarmonizedVendors,
			waterfallFilter,
			segmentName,
			isCustom,
		} = contextParams;

		const { customFiltersValues, customFilters } = this.getCustomFilters();
		const customFiltersValuesArray = convertToArray(customFiltersValues);
		const hierarchyArray = isEmpty(hierarchy)
			? measures
			: convertToArray(hierarchy);

		const grouppingNamesMap = {};
		const extraFilters = {};
		const chartFilterValues = Object.keys(chartFilterColumns).concat([
			'customColumnKey',
			'customColumnValue',
		]);
		chartFilterValues.forEach(key => {
			const value = contextParams[key];
			if (!!value) {
				extraFilters[key] = isEmpty(value) ? null : value;
			}
		});

		if (waterfallFilter) {
			extraFilters[waterfallFilter[0]] = waterfallFilter[1];
		}

		const filtersData = {
			dateFrom,
			dateTo,
			harmonized: showHarmonizedVendors,
			segmentName: isEmpty(segmentName) ? null : segmentName,
			filters: [
				...convertToArray(customFilters)
					.map((item, index) => {
						const customFilterOriginal = customFilterOptions.find(
							customItem => customItem.name === item
						);

						return {
							columnName: customFilterOriginal
								? customFilterOriginal.columnName
								: item,
							value: customFiltersValuesArray[index],
						};
					})
					.map(({ value, ...rest }) => ({
						...rest,
						value: isEmpty(value) ? '' : value,
					}))
					// group filters on same columnName to have value as array
					.reduce((acc, { columnName, value }) => {
						if (grouppingNamesMap[columnName]) {
							grouppingNamesMap[columnName].value = Array.isArray(
								grouppingNamesMap[columnName].value
							)
								? [...grouppingNamesMap[columnName].value, value]
								: [grouppingNamesMap[columnName].value, value];

							return acc;
						} else {
							const filter = { columnName, value };
							grouppingNamesMap[columnName] = filter;

							return [...acc, filter];
						}
					}, []),
				{ columnName: 'sourceable', value: showNonSourceable !== 'true' },
			],
			hierarchy: hierarchyArray,
			...extraFilters,
		};

		return (
			<React.Fragment>
				{!isTaxonomyTableReady ? (
					<LoaderContainer>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoaderContainer>
				) : null}
				<TableContainer isVisible={isTaxonomyTableReady}>
					{!isCategoriesOptionsLoading && (
						<TableView
							contextParams={contextParams}
							filtersData={filtersData}
							isCustomCategory={isCustomCategory(dimensions) || isCustom}
							openCellModalEditor={this.openCellModalEditor}
							openCategorisationModal={this.openCategorisationModal}
							clearApiCache={clearApiCache}
							apiCacheTrigger={apiCacheTrigger}
							setIsReady={this.setIsTaxonomyTableReady}
							showErrorMessage={this.showErrorMessage}
							permissions={permissions || []}
							timestamp={
								status.lastPublishedDate || status.lastSuccessClassificationDate
							}
							customFilterOptions={customFilterOptions}
							companiesList={companiesList}
							categoriesOptions={categoriesOptions}
						/>
					)}
				</TableContainer>
			</React.Fragment>
		);
	}

	handleNavigationBackButtonClick = () => {
		const { location, history } = this.props;
		const { prevPageUrl } = location.state;

		const locationStateCleanup = {
			prevPageUrl: null,
			selectedDimensionKey: null,
			selectedDimensionName: null,
		};

		history.push(prevPageUrl, locationStateCleanup);
	};

	render() {
		const { location, companiesList, hideNav } = this.props;
		const {
			isCellModalVisible,
			cellModalEditorProps,
			isErrorMessageVisible,
			errorMessageText,
			isCategorisationModalVisible,
		} = this.state;

		let navPanelProps = {};

		if (location && location.pathname && location.pathname.includes('/pmi/')) {
			navPanelProps = {
				showPMINav: true,
				disableNegativeSpendSelect: true,
				companiesList: companiesList,
			};
		}

		if (location && location.state) {
			const { prevPageUrl, transactionsTitle } = location.state;

			if (prevPageUrl) {
				navPanelProps = {
					...navPanelProps,
					transactionsTitle,
					onBackButtonClick: this.handleNavigationBackButtonClick,
				};
			}
		}

		return (
			<Wrapper>
				{!hideNav && <SecondaryNav {...navPanelProps} />}
				{this.renderTable()}
				{isCellModalVisible && (
					<CellModalEditor
						onClose={this.closeCellModalEditor}
						{...cellModalEditorProps}
					/>
				)}
				{isCategorisationModalVisible && (
					<CategorizationDialog
						onClose={this.closeCellModalEditor}
						{...cellModalEditorProps}
					/>
				)}
				{isErrorMessageVisible && (
					<ErrorMessageModal
						message={errorMessageText}
						onClose={this.hideErrorMessage}
					/>
				)}
			</Wrapper>
		);
	}
}

export default props => (
	<PermissionsContext.Consumer>
		{({ permissions }) => (
			<UploadStateContext.Consumer>
				{({ status }) => (
					<ApiDataContext.Consumer>
						{({ clearApiCache, apiCacheTrigger }) => (
							<MetadataContext.Consumer>
								{({ metadata }) => (
									<SettingsContext.Consumer>
										{({ customFilters, companiesList }) => (
											<QueryStringContext.Consumer>
												{queryStringParams => {
													const contextParams = { ...queryStringParams };

													if (contextParams.selectedDimensionKey) {
														contextParams[contextParams.selectedDimensionKey] =
															contextParams.selectedDimensionName;
													}

													return (
														<PivotTable
															{...props}
															contextParams={contextParams}
															dimensionOptions={metadata.dimensions}
															customFilterOptions={customFilters}
															clearApiCache={clearApiCache}
															apiCacheTrigger={apiCacheTrigger}
															permissions={permissions}
															status={status}
															companiesList={companiesList}
														/>
													);
												}}
											</QueryStringContext.Consumer>
										)}
									</SettingsContext.Consumer>
								)}
							</MetadataContext.Consumer>
						)}
					</ApiDataContext.Consumer>
				)}
			</UploadStateContext.Consumer>
		)}
	</PermissionsContext.Consumer>
);
